import React, { useState, useRef, useEffect } from 'react';
import { API_URL } from '../config';
import './AIAssistant.css';

function AIAssistant({ lessonContext }) {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const quickQuestions = [
        { text: "Explain this differently", icon: "🤔" },
        { text: "Give me an example", icon: "💡" },
        { text: "Why does this matter?", icon: "🎯" },
        { text: "Can you simplify this?", icon: "✨" }
    ];

    const handleSendMessage = async (messageText = null) => {
        const question = messageText || input.trim();
        if (!question || isLoading) return;

        const userMessage = { role: 'user', content: question };
        setMessages(prev => [...prev, userMessage]);
        setInput('');
        setIsLoading(true);

        try {
            const response = await fetch(`${API_URL}/api/lesson-assistant`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    question,
                    lessonContext
                })
            });

            const data = await response.json();

            if (data.success) {
                const aiMessage = { role: 'assistant', content: data.answer };
                setMessages(prev => [...prev, aiMessage]);
            } else {
                throw new Error(data.error || 'Failed to get response');
            }
        } catch (error) {
            console.error('Error:', error);
            const errorMessage = {
                role: 'assistant',
                content: 'Sorry, I had trouble answering that. Please try again!'
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleQuickQuestion = (question) => {
        handleSendMessage(question);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    return (
        <div className={`ai-assistant ${isOpen ? 'open' : ''}`}>
            {/* Floating Button */}
            <button
                className={`ai-assistant-toggle ${isOpen ? 'active' : ''}`}
                onClick={() => setIsOpen(!isOpen)}
                title="AI Learning Assistant"
            >
                {isOpen ? '✕' : '🤖'}
            </button>

            {/* Chat Panel */}
            {isOpen && (
                <div className="ai-assistant-panel">
                    {/* Header */}
                    <div className="ai-assistant-header">
                        <div className="assistant-avatar">🤖</div>
                        <div className="assistant-info">
                            <h3>AI Learning Assistant</h3>
                            <p>Ask me anything about this lesson!</p>
                        </div>
                    </div>

                    {/* Messages */}
                    <div className="ai-assistant-messages">
                        {messages.length === 0 && (
                            <div className="welcome-message">
                                <div className="welcome-icon">👋</div>
                                <h4>Hey there!</h4>
                                <p>I'm here to help you understand this lesson better. Ask me anything or try a quick question below!</p>
                            </div>
                        )}

                        {messages.map((msg, index) => (
                            <div
                                key={index}
                                className={`message ${msg.role === 'user' ? 'user-message' : 'ai-message'}`}
                            >
                                {msg.role === 'assistant' && (
                                    <div className="message-avatar">🤖</div>
                                )}
                                <div className="message-content">
                                    {msg.content}
                                </div>
                                {msg.role === 'user' && (
                                    <div className="message-avatar user-avatar">👤</div>
                                )}
                            </div>
                        ))}

                        {isLoading && (
                            <div className="message ai-message">
                                <div className="message-avatar">🤖</div>
                                <div className="message-content typing">
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                </div>
                            </div>
                        )}

                        <div ref={messagesEndRef} />
                    </div>

                    {/* Quick Questions */}
                    {messages.length === 0 && (
                        <div className="quick-questions">
                            {quickQuestions.map((q, index) => (
                                <button
                                    key={index}
                                    className="quick-question-btn"
                                    onClick={() => handleQuickQuestion(q.text)}
                                    disabled={isLoading}
                                >
                                    <span className="quick-q-icon">{q.icon}</span>
                                    <span>{q.text}</span>
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Input */}
                    <div className="ai-assistant-input">
                        <textarea
                            placeholder="Ask a question about this lesson..."
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyPress={handleKeyPress}
                            disabled={isLoading}
                            rows="1"
                        />
                        <button
                            onClick={() => handleSendMessage()}
                            disabled={!input.trim() || isLoading}
                            className="send-btn"
                        >
                            <span>➤</span>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AIAssistant;
